import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  ExternalLink, 
  Package, 
  ShieldX, 
  CheckCircle, 
  Loader2,
  Truck,
  Info,
  AlertCircle
} from 'lucide-react';
import OrderStatusBadge from './OrderStatusBadge';
import { Order } from './order-types';
import ShippingDetailsDisplay from './ShippingDetails';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog';

interface PurchaseCardProps {
  purchase: Order;
  confirmingDelivery: string | null;
  confirmingReceipt: string | null;
  onViewListing: (listingId: number) => void;
  onViewSellerProfile: (sellerId: string) => void;
  onContactSeller: (order: Order) => void;
  onCancelOrder: (orderId: string) => void;
  onConfirmDelivery: (orderId: string) => void;
  onConfirmReceipt: (orderId: string) => void;
}

const PurchaseCard = ({
  purchase,
  confirmingDelivery,
  confirmingReceipt,
  onViewListing,
  onViewSellerProfile,
  onContactSeller,
  onCancelOrder,
  onConfirmDelivery,
  onConfirmReceipt
}: PurchaseCardProps) => {
  const isConfirmingDelivery = confirmingDelivery === purchase.id;
  const isConfirmingReceipt = confirmingReceipt === purchase.id;
  const [showShippingInfo, setShowShippingInfo] = useState(false);
  
  const formatCurrency = (amount: number, currency: string) => {
    return `${amount.toFixed(2)} ${currency}`;
  };
  
  const timeAgo = (date: string) => {
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true });
    } catch (e) {
      return 'Unknown date';
    }
  };
  
  const canConfirmDelivery = purchase.status === 'shipped';
  const canConfirmReceipt = purchase.status === 'delivered';
  const canCancel = ['pending', 'processing'].includes(purchase.status);
  const hasShippingDetails = purchase.status === 'shipped' && purchase.shipping_details;
  const isAwaitingCancellation = purchase.status === 'cancelled' && purchase.requires_confirmation;
  
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4 md:p-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Image and title */}
          <div className="flex-shrink-0">
            <div 
              className="w-20 h-20 md:w-32 md:h-32 bg-gray-200 rounded-md overflow-hidden cursor-pointer"
              onClick={() => purchase.listings && onViewListing(purchase.listings.id)}
            >
              {purchase.listings && purchase.listings.images && purchase.listings.images[0] ? (
                <img 
                  src={purchase.listings.images[0]} 
                  alt={purchase.listings.title} 
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400">
                  <Package size={24} />
                </div>
              )}
            </div>
          </div>
          
          {/* Order details */}
          <div className="flex-1">
            <div className="flex flex-col md:flex-row md:justify-between">
              <div>
                <h3 
                  className="text-base md:text-lg font-medium cursor-pointer hover:underline line-clamp-2"
                  onClick={() => purchase.listings && onViewListing(purchase.listings.id)}
                >
                  {purchase.listings?.title || 'Unknown product'}
                </h3>
                <div className="mt-1 text-xs md:text-sm text-gray-500">
                  <OrderStatusBadge status={purchase.status} />
                  {isAwaitingCancellation && (
                    <span className="ml-2 text-amber-600">(Duke pritur konfirmimin e shitësit)</span>
                  )}
                  <span className="ml-2">{timeAgo(purchase.created_at)}</span>
                </div>
              </div>
              <div className="text-left md:text-right mt-2 md:mt-0">
                <div className="font-bold">
                  {formatCurrency(purchase.amount, purchase.currency)}
                </div>
                <div className="text-xs md:text-sm text-gray-500">
                  {purchase.shipping_method || 'Standard Shipping'}
                </div>
              </div>
            </div>
            
            {/* Cancellation reason */}
            {purchase.status === 'cancelled' && purchase.cancellation_reason && (
              <div className="mt-3 p-2 bg-red-50 border border-red-100 rounded-md">
                <div className="flex items-start gap-2">
                  <AlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-700">Arsyeja e anulimit:</p>
                    <p className="text-sm text-red-600">{purchase.cancellation_reason}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Seller info */}
            {purchase.seller_profile && (
              <div className="mt-3 md:mt-4 flex flex-wrap items-center gap-2">
                <Avatar className="h-5 w-5 md:h-6 md:w-6">
                  <AvatarFallback>
                    {purchase.seller_profile.full_name?.charAt(0) || 'S'}
                  </AvatarFallback>
                </Avatar>
                <span className="text-xs md:text-sm font-medium">
                  {purchase.seller_profile.full_name || 'Unknown Seller'}
                </span>
                <Button 
                  variant="link" 
                  className="p-0 h-auto text-xs md:text-sm" 
                  onClick={() => onViewSellerProfile(purchase.seller_profile?.id || '')}
                >
                  Shiko Profilin
                </Button>
                <Button 
                  variant="link" 
                  className="p-0 h-auto text-xs md:text-sm" 
                  onClick={() => onContactSeller(purchase)}
                >
                  Kontakto
                </Button>
              </div>
            )}
            
            {/* Shipping info button */}
            {hasShippingDetails && (
              <div className="mt-3">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="text-xs flex items-center gap-1 bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                  onClick={() => setShowShippingInfo(true)}
                >
                  <Truck size={14} className="mr-1" />
                  Informacion Transporti
                </Button>
              </div>
            )}
          </div>
        </div>
        
        {/* Action buttons */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center pt-4 mt-4 border-t gap-2 md:gap-0">
          <Button 
            variant="outline" 
            size="sm"
            className="flex items-center gap-1 text-xs md:text-sm w-full md:w-auto"
            onClick={() => purchase.listings && onViewListing(purchase.listings.id)}
          >
            <ExternalLink size={14} />
            Shiko Produktin
          </Button>
          
          <div className="flex flex-wrap md:flex-nowrap gap-2 w-full md:w-auto justify-end">
            {canConfirmDelivery && (
              <Button 
                variant="default"
                size="sm"
                className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700 text-xs md:text-sm w-full md:w-auto"
                onClick={() => onConfirmDelivery(purchase.id)}
                disabled={isConfirmingDelivery}
              >
                {isConfirmingDelivery ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Duke konfirmuar...
                  </>
                ) : (
                  <>
                    <Package size={14} />
                    Konfirmo Dorëzimin
                  </>
                )}
              </Button>
            )}
            
            {canConfirmReceipt && (
              <Button 
                variant="default"
                size="sm"
                className="flex items-center gap-1 bg-green-600 hover:bg-green-700 text-xs md:text-sm w-full md:w-auto"
                onClick={() => onConfirmReceipt(purchase.id)}
                disabled={isConfirmingReceipt}
              >
                {isConfirmingReceipt ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Duke konfirmuar...
                  </>
                ) : (
                  <>
                    <CheckCircle size={14} />
                    Konfirmo Marrjen
                  </>
                )}
              </Button>
            )}
            
            {canCancel && (
              <Button 
                variant="destructive"
                size="sm"
                className="flex items-center gap-1 text-xs md:text-sm w-full md:w-auto"
                onClick={() => onCancelOrder(purchase.id)}
              >
                <ShieldX size={14} />
                Anulo Porosinë
              </Button>
            )}
          </div>
        </div>
      </CardContent>

      {/* Shipping Info Dialog */}
      <Dialog open={showShippingInfo} onOpenChange={setShowShippingInfo}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Informacioni i Transportit</DialogTitle>
            <DialogDescription>
              Detajet e dërgimit të produktit nga shitësi
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4">
            <ShippingDetailsDisplay shippingDetails={purchase.shipping_details} />
            
            {purchase.status === 'shipped' && (
              <div className="mt-6 flex justify-end">
                <Button 
                  onClick={() => onConfirmDelivery(purchase.id)}
                  disabled={isConfirmingDelivery}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {isConfirmingDelivery ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Duke konfirmuar...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={16} className="mr-2" />
                      Konfirmo Marrjen e Produktit
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default PurchaseCard;
